// ============================================================================
// CALENDAR MODULE — data layer (real FastAPI + MongoDB backend, Phase 2)
// Eventi + connessione Google Calendar (placeholder). To Do planning resta in
// notesData (Module D). Nessuna API reale Google/Gmail/WhatsApp.
// Cache idratata al bootstrap; mutazioni ottimistiche + persistenza in background.
// ============================================================================
import { api } from "@/lib/api";
import { listTeam } from "@/lib/teamData";
import { getActiveRegistryClients, getRegistryClient } from "@/lib/clientRegistry";

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const APPOINTMENT_TYPES = {
  client_call: "Call cliente",
  client_videocall: "Videocall cliente",
  appointment: "Appuntamento",
  appointment_offsite: "Appuntamento fuori sede",
  internal_meeting: "Riunione interna",
  close_agenda: "Chiudi agenda",
};
// Etichette per la visualizzazione, incluse le tipologie storiche (eventi già creati).
export const APPOINTMENT_TYPE_LABELS = {
  ...APPOINTMENT_TYPES,
  monthly_alignment: "Monthly Alignment Call",
  campaign_review: "Campaign Review",
  sales_call: "Sales Call",
  support_call: "Support Call",
  other: "Altro",
};
export const EVENT_STATUS = {
  scheduled: { label: "Programmato", tone: "info" },
  confirmed: { label: "Confermato", tone: "ok" },
  rescheduled: { label: "Riprogrammato", tone: "warning" },
  completed: { label: "Completato", tone: "neutral" },
  cancelled: { label: "Annullato", tone: "warning" },
  no_show: { label: "No Show", tone: "warning" },
};
export const REMINDER_CHANNELS = { whatsapp: "WhatsApp", email: "Email", both: "Entrambi" };
export const REMINDER_TIMINGS = { "24h": "24 ore prima", "12h": "12 ore prima", "3h": "3 ore prima", "1h": "1 ora prima", custom: "Personalizzato" };
export const CONN_STATUS = {
  connected: { label: "Connesso", tone: "ok" }, not_connected: { label: "Non connesso", tone: "neutral" },
  expired: { label: "Scaduto", tone: "warning" }, error: { label: "Errore", tone: "warning" },
};

const PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#06b6d4", "#8b5cf6", "#ef4444"];
export const teamMembers = () => listTeam().map((m, i) => ({ ...m, color: PALETTE[i % PALETTE.length] }));
export const memberColor = (id) => { const m = teamMembers().find((x) => x.id === id); return m?.color || "#6366f1"; };
export const memberName = (id) => teamMembers().find((x) => x.id === id)?.name || "—";

export const clientOptions = () => getActiveRegistryClients().map((c) => ({ id: c.id, name: c.client_name }));
export const clientContact = (clientId) => { const c = getRegistryClient(clientId); return c ? { email: c.main_contact_email || "", phone: c.main_contact_phone || "", contact: c.main_contact_name || "" } : { email: "", phone: "", contact: "" }; };

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
export const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - day); return x; };
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
export const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
export const isToday = (d) => sameDay(d, new Date());
export const hhmm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
export const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
export const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
export const atHour = (day, hour, min = 0) => { const x = new Date(day); x.setHours(hour, min, 0, 0); return x; };

// ---------------------------------------------------------------------------
// SEED
// ---------------------------------------------------------------------------
const seedEvents = () => {
  const team = listTeam();
  const clients = getActiveRegistryClients();
  const mon = startOfWeek(new Date());
  const at = (dayOffset, h, m = 0) => { const x = addDays(mon, dayOffset); x.setHours(h, m, 0, 0); return x.toISOString(); };
  const mk = (o) => {
    const c = o.client_id ? clients.find((x) => x.id === o.client_id) : null;
    return {
      id: uid("evt"), client_id: o.client_id || null, title: o.title,
      contact_name: c?.main_contact_name || o.contact_name || "", contact_email: c?.main_contact_email || "", contact_phone: c?.main_contact_phone || "",
      assigned_user_id: o.assigned_user_id, assigned_user_name: team.find((t) => t.id === o.assigned_user_id)?.name || "",
      additional_user_ids: o.additional_user_ids || [], appointment_type: o.appointment_type || "client_call",
      status: o.status || "scheduled", start_time: o.start, end_time: o.end,
      location: o.location || "", meeting_link: o.meeting_link || "", video_call_enabled: !!o.meeting_link,
      notes: o.notes || "",
      confirmation: { whatsapp_sent: o.status === "confirmed", email_sent: o.status === "confirmed" },
      reminder: { enabled: true, channel: "whatsapp", timing: "24h", custom_minutes: null, template_id: null, custom_message: "" },
      google_calendar_event_id: null, created_by: "u_andrea", created_at: now(), updated_at: now(),
    };
  };
  const c0 = clients[0]?.id, c1 = clients[1]?.id, c2 = clients[2]?.id;
  const t = team.map((x) => x.id);
  return [
    mk({ title: "Call allineamento mensile", client_id: c0, assigned_user_id: t[0], appointment_type: "monthly_alignment", status: "confirmed", start: at(0, 10), end: at(0, 11), meeting_link: "https://meet.google.com/mock-abc" }),
    mk({ title: "Riunione interna team", assigned_user_id: t[1], additional_user_ids: [t[0], t[2]].filter(Boolean), appointment_type: "internal_meeting", start: at(0, 15), end: at(0, 16) }),
    mk({ title: "Review campagne", client_id: c1, assigned_user_id: t[2] || t[0], appointment_type: "campaign_review", status: "scheduled", start: at(2, 11, 30), end: at(2, 12, 30) }),
    mk({ title: "Sales call nuovo lead", client_id: c2, assigned_user_id: t[0], appointment_type: "sales_call", start: at(3, 9), end: at(3, 9, 45), meeting_link: "https://meet.google.com/mock-sales" }),
    mk({ title: "Support call", client_id: c0, assigned_user_id: t[1], appointment_type: "support_call", status: "confirmed", start: at(4, 14), end: at(4, 14, 30) }),
  ];
};

// ---------------------------------------------------------------------------
// CACHE (backend-hydrated)
// ---------------------------------------------------------------------------
let _events = [];
let _conns = {};              // { userId: connection }
let _hydrated = false;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeCalendar = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isCalendarReady = () => _hydrated;

export async function hydrateCalendar() {
  try {
    const [events, conn] = await Promise.all([
      api.get("/calendar/events").then((r) => r.data).catch(() => []),
      api.get("/calendar/connection").then((r) => r.data).catch(() => null),
    ]);
    _events = Array.isArray(events) ? events : [];
    if (conn?.user_id) _conns[conn.user_id] = conn;
  } catch { /* noop */ } finally { _hydrated = true; notify(); }
}
export const resetCalendar = () => { _events = []; _conns = {}; _hydrated = false; };
const resync = () => { hydrateCalendar(); };

// ---------------------------------------------------------------------------
// EVENTS
// ---------------------------------------------------------------------------
export const listEvents = () => _events.slice().sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
export const getEvent = (id) => _events.find((e) => e.id === id) || null;

export const createEvent = (data) => {
  const contact = data.client_id ? clientContact(data.client_id) : {};
  const id = uid("evt");
  const e = {
    id, client_id: data.client_id || null, title: data.title || "Appuntamento",
    contact_name: data.contact_name || contact.contact || "", contact_email: data.contact_email || contact.email || "", contact_phone: data.contact_phone || contact.phone || "",
    assigned_user_id: data.assigned_user_id || null, assigned_user_name: memberName(data.assigned_user_id),
    calendar_source: data.calendar_source || data.assigned_user_id || null,
    additional_user_ids: data.additional_user_ids || [], appointment_type: data.appointment_type || "client_call",
    status: data.status || "scheduled", start_time: data.start_time, end_time: data.end_time,
    location: data.location || "", meeting_link: data.meeting_link || "", video_call_enabled: !!data.video_call_enabled,
    notes: data.notes || "",
    confirmation: { whatsapp_sent: false, email_sent: false },
    reminder: data.reminder || { enabled: false, channel: "whatsapp", timing: "24h", custom_minutes: null, template_id: null, custom_message: "" },
    google_calendar_event_id: null, created_by: "u_andrea", created_at: now(), updated_at: now(),
  };
  _events = [..._events, e];
  notify();
  api.post("/calendar/events", { ...e, id }).then(({ data: d }) => { _events = _events.map((x) => (x.id === id ? d : x)); notify(); }).catch(resync);
  return id;
};

export const updateEvent = (id, patch) => {
  const i = _events.findIndex((e) => e.id === id);
  if (i > -1) {
    const merged = { ..._events[i], ...patch, assigned_user_name: memberName(patch.assigned_user_id || _events[i].assigned_user_id), updated_at: now() };
    _events = _events.map((e) => (e.id === id ? merged : e));
    notify();
  }
  api.patch(`/calendar/events/${id}`, patch).catch(resync);
};
export const setEventStatus = (id, status) => updateEvent(id, { status });
export const removeEvent = (id) => {
  _events = _events.filter((e) => e.id !== id);
  notify();
  api.delete(`/calendar/events/${id}`).catch(resync);
};
export const sendConfirmation = (id, channel, message = "") => {
  const e = _events.find((x) => x.id === id);
  if (e) {
    const conf = {
      whatsapp_sent: channel === "email" ? e.confirmation?.whatsapp_sent : true,
      email_sent: channel === "whatsapp" ? e.confirmation?.email_sent : true,
    };
    updateEventLocal(id, { confirmation: conf, status: e.status === "scheduled" ? "confirmed" : e.status });
  }
  api.post(`/calendar/events/${id}/confirmation`, { channel, message }).then(({ data: d }) => { _events = _events.map((x) => (x.id === id ? d : x)); notify(); }).catch(resync);
};
const updateEventLocal = (id, patch) => { _events = _events.map((e) => (e.id === id ? { ...e, ...patch, updated_at: now() } : e)); notify(); };

export const clientEvents = (clientId) => listEvents().filter((e) => e.client_id === clientId);

// ---------------------------------------------------------------------------
// GOOGLE CALENDAR — sincronizzazione bidirezionale (import da Google)
// ---------------------------------------------------------------------------
export const googleCalendarStatus = () =>
  api.get("/calendar/google-status").then((r) => r.data).catch(() => ({ connected: false }));

export const pullFromGoogle = async () => {
  const { data } = await api.post("/calendar/sync/pull");
  await hydrateCalendar(); // re-idrata per mostrare gli eventi importati
  return data;
};

// ---------------------------------------------------------------------------
// CALENDAR CONNECTIONS (per user) — placeholder Google Calendar (no OAuth)
// ---------------------------------------------------------------------------
export const getCalendarConnection = (userId) => _conns[userId] || { status: "not_connected", connected_email: null, primary_calendar_id: null, last_sync_at: null, last_error: null };
export const connectGoogleCalendar = (userId, email) => {
  _conns = { ..._conns, [userId]: { status: "connected", connected_email: email, primary_calendar_id: "primary", last_sync_at: now(), last_error: null } };
  notify();
  api.post("/calendar/connection", { email }).then(({ data }) => { _conns = { ..._conns, [userId]: data }; notify(); }).catch(resync);
};
export const disconnectGoogleCalendar = (userId) => {
  _conns = { ..._conns, [userId]: { status: "not_connected", connected_email: null, primary_calendar_id: null, last_sync_at: null, last_error: null } };
  notify();
  api.delete("/calendar/connection").catch(resync);
};
